import { Fragment, useContext, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import Header from 'components/Header'
import { deleteBoxItem, getBoxById, updateBoxItem } from 'api/box'
import { LoadingContext } from 'contexts/LoadingContextProvider'
import { getLocationNameById } from 'utils'
import { SnackContext } from 'contexts/SnackContextProvider'
import { SnackbarSeverity } from 'constant'

const BoxDetail = () => {
  const { boxId } = useParams()
  const { handleOpenSnackbar } = useContext(SnackContext)
  const [selectedBox, setSelectedBox] = useState<any>(null)
  const { setLoading } = useContext(LoadingContext)
  const [editItem, setEditItem] = useState<any>(null)
  const [editQuantity, setEditQuantity] = useState<number>(0)
  const [openDialog, setOpenDialog] = useState(false)

  const fetchBox = async () => {
    setLoading(true)
    try {
      const result = await getBoxById(`${boxId}`)
      if (result.data.success) {
        setSelectedBox(result.data.box)
      }
    } catch (error) {
      console.error('Error fetching box:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenEdit = (item: any) => {
    setEditItem(item)
    setEditQuantity(item.quantity)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditItem(null)
    setEditQuantity(0)
  }
  const handleSaveQuantity = async () => {
    if (!editItem) return
    try {
      const result = await updateBoxItem(editItem._id, editQuantity)
      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Quantity updated successfully'
        )
        fetchBox()
      } else {
        handleOpenSnackbar(SnackbarSeverity.Error, 'Failed to update quantity')
      }
    } catch (err: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        err.message || 'Failed to update quantity'
      )
    } finally {
      handleCloseDialog()
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    try {
      const result = await deleteBoxItem(itemId)
      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Item deleted successfully'
        )
        fetchBox()
      } else {
        handleOpenSnackbar(SnackbarSeverity.Error, 'Failed to delete item')
      }
    } catch (err: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        err.message || 'Failed to delete item'
      )
    }
  }

  useEffect(() => {
    fetchBox()
  }, [])

  if (!selectedBox) {
    return (
      <Box>
        <Header />
        <Typography variant='h6'>Loading...</Typography>
      </Box>
    )
  }

  return (
    <>
      <Header />
      <Box sx={{ p: 3 }}>
        <Typography variant='h4' gutterBottom>
          Box Details
        </Typography>
        <Paper sx={{ padding: '10px', marginBottom: '40px' }}>
          <Typography variant='subtitle1'>
            <strong>Box Code:</strong> {selectedBox.boxCode}
          </Typography>
          <Typography variant='subtitle1'>
            <strong>Company:</strong> {selectedBox.company}
          </Typography>
          <Typography variant='subtitle1'>
            <strong>Location:</strong>{' '}
            {getLocationNameById(selectedBox.locationId)}
          </Typography>
        </Paper>
        <Typography variant='h5' gutterBottom>
          Box Items
        </Typography>
        {selectedBox.boxItems && selectedBox.boxItems.length > 0 ? (
          <Paper>
            <List>
              {selectedBox.boxItems.map((item: any, index: number) => (
                <Fragment key={item._id}>
                  <ListItem
                    secondaryAction={
                      <>
                        <IconButton onClick={() => handleOpenEdit(item)}>
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteItem(item._id)}>
                          <Delete />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemText
                      primary={`Name: ${item.name}`}
                      secondary={`Quantity: ${item.quantity}`}
                    />
                  </ListItem>
                  {index < selectedBox.boxItems.length - 1 && <Divider />}
                </Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography variant='body1'>No Box Items available.</Typography>
        )}
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Edit Quantity</DialogTitle>
        <DialogContent>
          <TextField
            type='number'
            value={editQuantity}
            onChange={e => setEditQuantity(Number(e.target.value))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveQuantity} variant='contained'>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BoxDetail
